import z from 'zod';
import { fileSchema, fileOwnerTypeSchema, fileStatusSchema } from './schemas';

export type File = z.infer<typeof fileSchema>;
export type FileOwnerType = z.infer<typeof fileOwnerTypeSchema>;
export type FileStatus = z.infer<typeof fileStatusSchema>;

export type FileTypes = {
	[key: string]: string;
};

export const CompanyVcaTypes = Object.freeze([
	'vca-company',
	'vca-company-*',
	'vca-company-**',
	'vca-company-p',
	'vca-company-u',
]);

export const UserVcaTypes = Object.freeze([
	'vca-user',
	'vca-user-vol',
	'vca-user-basis',
	'ssc-user-vol',
	'ssc-user-basis',
]);

/**
 * Document types that satisfy the VCA-P (Petrochemie) requirement.
 * Includes VCA-P base type, its hierarchy (lower levels), and equivalent international safety certificates.
 * Based on CERTIFICATE_EQUIVALENCE and CERTIFICATE_HIERARCHY from safetyCertificates.js
 */
export const VcaCompanyPAllowedTypes = Object.freeze([
	'vca-company-p', // Base type
	'ssc-company-p', // Equivalent certificate
	'mase', // Equivalent international certificate
	'iso-45001', // Equivalent international certificate
]);

export const VcaCompanyDoubleStarAllowedTypes = Object.freeze(['vca-company-**', 'ssc-company-**']);

export const VcaCompanySingleStarAllowedTypes = Object.freeze(['vca-company-*', 'ssc-company-*']);

export const VcaCompanyUAllowedTypes = Object.freeze(['vca-company-u', 'ssc-company-u']);

export const AllVcaCompanyDocTypes = Object.freeze([
	...VcaCompanyPAllowedTypes,
	...VcaCompanyDoubleStarAllowedTypes,
	...VcaCompanySingleStarAllowedTypes,
	...VcaCompanyUAllowedTypes,
]);

export const VcaUserVolAllowedTypes = Object.freeze(['vca-user-vol', 'ssc-user-vol']);

export const VcaUserBasisAllowedTypes = Object.freeze(['vca-user-basis', 'ssc-user-basis']);

export const UserVcaRequirementKeys = Object.freeze(['user-vca-vol-checked', 'user-vca-basis-checked']);

/**
 * Maps each user VCA file type to the requirement keys it can satisfy.
 * VOL-level docs satisfy both VOL and Basis requirements; Basis-level docs only satisfy Basis.
 */
export const UserVcaTypeToRequirementKeys: Readonly<Record<string, readonly string[]>> = Object.freeze({
	'vca-user': Object.freeze(['user-vca-basis-checked']),
	'vca-user-basis': Object.freeze(['user-vca-basis-checked']),
	'vca-user-vol': Object.freeze(['user-vca-vol-checked', 'user-vca-basis-checked']),
	'ssc-user-basis': Object.freeze(['user-vca-basis-checked']),
	'ssc-user-vol': Object.freeze(['user-vca-vol-checked', 'user-vca-basis-checked']),
});
